import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { DataSourceLoadOptions, RecommendationToken, RecommendationAdditionalFiltering } from '@sleed/bizcommerce-api';
import { recommendationsApi } from '~/lib/api/client';
import { endOfDayIso, type RecommendationKind } from '~/lib/reviews/reviews';

export interface RecommendationsListParams {
	skip?: number;
	take?: number;
	search?: string;
	dateFrom?: string | null;
	dateTo?: string | null;
}

export interface RecommendationsListResult {
	items: RecommendationToken[];
	totalCount: number;
}

export const recommendationKeys = {
	all: ['recommendations'] as const,
	list: (kind: RecommendationKind, params: unknown) => ['recommendations', kind, 'list', params] as const
};

function buildFiltering(p: RecommendationsListParams): RecommendationAdditionalFiltering {
	const f: RecommendationAdditionalFiltering = {};
	if (p.search?.trim()) f.searchText = p.search.trim();
	if (p.dateFrom) f.RecommendationDate_from = p.dateFrom;
	if (p.dateTo) f.RecommendationDate_to = endOfDayIso(p.dateTo);
	return f;
}

/**
 * Server-paged recommendations (getAllCustomerRecommendation / getAllContributorRecommendation, the same DevExtreme
 * loadOptions envelope as the reviews grid; RecommendationQueryResponse { data, totalCount }). The server lists the
 * newest first when no sort is sent.
 */
export function useRecommendationsList(kind: RecommendationKind, params: RecommendationsListParams = {}) {
	const { skip = 0, take = 25, ...rest } = params;
	return useQuery<RecommendationsListResult>({
		queryKey: recommendationKeys.list(kind, { skip, take, ...rest }),
		placeholderData: keepPreviousData,
		queryFn: async () => {
			const loadOptions: DataSourceLoadOptions = { skip, take, requireTotalCount: true };
			const request = { loadOptions, additionalFiltering: buildFiltering(params) };
			const res =
				kind === 'customer'
					? await recommendationsApi.getAllCustomerRecommendation(request)
					: await recommendationsApi.getAllContributorRecommendation(request);
			return { items: res.data?.data ?? [], totalCount: res.data?.totalCount ?? 0 };
		}
	});
}

export function useRecommendationMutations() {
	const qc = useQueryClient();
	const remove = useMutation({
		mutationFn: async (id: number) => (await recommendationsApi.remove(id)).data,
		onSuccess: () => qc.invalidateQueries({ queryKey: recommendationKeys.all })
	});
	return { remove };
}
